import {
  Box,
  Icon,
  Modal,
  ModalContent,
  useDisclosure,
} from "@chakra-ui/react";
import { SiChevronLeft } from "@medstonetech/slate-icons";
import { useToast } from "hooks";
import { genericErrors } from "messages";
import {
  SignedSection,
  useCreateSignedSection,
  useDeleteSignedSection,
  useGetSignedSection,
} from "modules";
import { useState, useEffect, ReactNode } from "react";
import {
  Button,
  PdfViewer,
  PdfViewerActions,
  PdfViewerActivePage,
  PdfViewerContainer,
  PdfViewerHeader,
  PdfViewerPageController,
  PdfViewerPreview,
} from "shared";
import { extractApiErrorMessage } from "utils";
import { AdditionalDocumentModal } from "../summary";

type FilePreviewProps = {
  file: File | string | Blob;
  setFile: (file: Nullable<string | File | Blob>) => void;
  isOpen: boolean;
  isFetching: boolean;
  onClose: () => void;
  encounterId: string;
  type: SignedSection;
};

function FilePreview({
  file,
  setFile,
  isOpen,
  isFetching,
  onClose,
  encounterId,
  type,
}: FilePreviewProps) {
  const toast = useToast();
  const [numPages, setNumPages] = useState(0);
  const [activePageNumber, setActivePageNumber] = useState(1);

  const { mutateAsync: deleteMot, isLoading: isDeleteLoading } =
    useDeleteSignedSection(encounterId);
  const { mutateAsync: upload, isLoading: isUploadLoading } =
    useCreateSignedSection(encounterId);

  const handleUpload = async (newFile: Nullable<string | File | Blob>) => {
    try {
      if (newFile instanceof File) {
        const formData = new FormData();
        formData.append("file", newFile as Blob, newFile.name);
        await upload({ type, input: formData });
        setFile(newFile);
        setNumPages(0);
        setActivePageNumber(1);
        return;
      }

      if (newFile instanceof Blob) {
        const formData = new FormData();
        formData.append("file", newFile, "file");
        await upload({ type, input: formData });
        setFile(newFile);
        setNumPages(0);
        setActivePageNumber(1);
        return;
      }
    } catch (error) {
      toast({
        description:
          extractApiErrorMessage(error) || genericErrors.unknownError,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMot({ type, pageNumber: activePageNumber });
      setNumPages(0);
      setActivePageNumber(1);
    } catch (error) {
      toast({
        description:
          extractApiErrorMessage(error) || genericErrors.unknownError,
      });
    }
  };

  let title = "";
  switch (type) {
    case "Discharge":
      title = "Discharge Forms - Signed";
      break;
    case "Transfer":
      title = "Transfer Forms - Signed";
      break;
    case "Observation":
      title = "Observation Forms - Signed";
      break;
    default:
      title = "Signed Section";
      break;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent minW="1570px" minH="75%" maxH="90%">
        <PdfViewer
          value={file}
          numPages={numPages}
          activePageNumber={activePageNumber}
          setNumPages={setNumPages}
          setActivePageNumber={setActivePageNumber}
          onDelete={handleDelete}
          onChange={handleUpload}
        >
          <PdfViewerContainer
            contentContainerProps={{ minH: "72vh", maxH: "92vh" }}
            header={
              <PdfViewerHeader
                title={title}
                leftElements={[
                  <Button
                    key="backButton"
                    size="iconSm"
                    variant="ghost"
                    color="blue"
                    onClick={onClose}
                    leftIcon={<Icon as={SiChevronLeft} />}
                  >
                    Back
                  </Button>,
                ]}
                rightElements={[
                  <PdfViewerActions
                    key="viewer-actions"
                    showDelete
                    showSubmit
                    isDeleteLoading={isDeleteLoading}
                    isSubmitLoading={isUploadLoading}
                  />,
                ]}
              />
            }
            isLoading={isFetching}
            activePage={<PdfViewerActivePage />}
            preview={<PdfViewerPreview />}
            pageController={<PdfViewerPageController />}
          />
        </PdfViewer>
      </ModalContent>
    </Modal>
  );
}

type MotButtonProps = {
  encounterId: string;
  type: SignedSection;
  icon: ReactNode;
};

function SignedSectionButton({ encounterId, type, icon }: MotButtonProps) {
  const toast = useToast();
  const disclosure = useDisclosure();
  const [file, setFile] = useState<Nullable<File | string | Blob>>();

  const {
    data,
    isLoading,
    isFetching,
    error: fileError,
  } = useGetSignedSection(encounterId, type);
  const { mutateAsync: upload, isLoading: isUploadLoading } =
    useCreateSignedSection(encounterId);

  const handleUpload = async (newFile: Nullable<string | File | Blob>) => {
    try {
      if (newFile instanceof File) {
        const formData = new FormData();
        formData.append("file", newFile);
        await upload({ type: type, input: formData, override: true });
        disclosure.onClose();
      }
    } catch (error) {
      toast({
        description:
          extractApiErrorMessage(error) || genericErrors.unknownError,
      });
    }
  };

  useEffect(() => {
    if (data && !fileError) {
      setFile(data.data);
    } else if (fileError) {
      setFile(null);
    }
  }, [data, fileError]);

  return (
    <Box w="36px">
      <Button
        zIndex={1000}
        h="30px"
        w="30px"
        minW="36px"
        maxW="32px"
        borderRadius="10px"
        colorScheme={file ? "green" : "red"}
        bgColor={file ? "green" : "red"}
        fontSize="15px"
        fontWeight="600"
        isLoading={isLoading || isUploadLoading}
        isDisabled={isLoading || isUploadLoading}
        onClick={(e) => {
          disclosure.onOpen();
          e.stopPropagation();
        }}
        display="flex"
        alignItems="center"
      >
        {icon}
      </Button>
      {!file && (
        <AdditionalDocumentModal
          {...disclosure}
          onChangeFile={handleUpload}
          directImport={type === "Discharge" || type === "Transfer"}
        />
      )}
      {file && (
        <FilePreview
          {...disclosure}
          file={file}
          setFile={setFile}
          isFetching={isFetching}
          encounterId={encounterId}
          type={type}
        />
      )}
    </Box>
  );
}

export { SignedSectionButton };
